import { api, responseErrorCheck } from './api'
import {
  EncodingValidationRequest,
  EncodingValidationResponse,
  EncodingCounters,
  Product,
  FindProductRequest,
  TagPasswordIdentifiers,
  TagPasswordResponse,
  TrmEncodingVerifyTag,
  TrmEncodingVerifyReplaceTag
} from 'stylewhere/api'

// aggiunge al payload il runId della sessione datadog, quando presente
const withRunId = <T extends Record<string, any>>(payload: T, runId?: string) =>
  runId ? { ...payload, runId } : payload

export class Encodings {
  static endpoint = 'api/v1/operations/encoding'
  static encodingEndpoint = 'api/v1/encoding'

  static async validate(validation: EncodingValidationRequest, runId?: string) {
    return api
      .post<EncodingValidationResponse>(`${this.endpoint}/validate`, withRunId(validation, runId))
      .then((res) => responseErrorCheck(res))
  }

  static async create(validation: EncodingValidationRequest, runId?: string) {
    return api
      .post<EncodingValidationResponse>(`${this.endpoint}/create`, withRunId(validation, runId))
      .then((res) => responseErrorCheck(res))
  }

  static async force_create(validation: EncodingValidationRequest, pin?: string, runId?: string) {
    const queryparams = pin && pin !== '' ? '?pin=' + pin : ''
    return api
      .post<EncodingValidationResponse>(`${this.endpoint}/create/force` + queryparams, withRunId(validation, runId))
      .then((res) => responseErrorCheck(res))
  }

  static async verify(validation: EncodingValidationRequest, runId?: string) {
    return api
      .post<EncodingValidationResponse>(`${this.endpoint}/verify`, withRunId(validation, runId))
      .then((res) => responseErrorCheck(res))
  }

  static async getProductionOrderRowCounter(productionOrderRowId: string) {
    return api.get<EncodingCounters>(`${this.endpoint}/counters?productionOrderRowId=${productionOrderRowId}`)
  }

  static async getProductionOrderCounter(productionOrderId: string) {
    return api.get<EncodingCounters>(`${this.endpoint}/counters?productionOrderId=${productionOrderId}`)
  }

  static async getWorkOrderCounter(workOrderId: string) {
    return api.get<EncodingCounters>(`${this.endpoint}/preencoding/counters?workOrderId=${workOrderId}`)
  }

  static async getWorkOrderRowCounter(workOrderRowId: string) {
    return api.get<EncodingCounters>(`${this.endpoint}/preencoding/counters?workOrderRowId=${workOrderRowId}`)
  }

  static async disassociate(
    body: { operationId: string; itemId?: string; itemIdentifierIds?: string[] },
    runId?: string
  ) {
    return api
      .post<EncodingCounters>(`${this.endpoint}/disassociate`, withRunId(body, runId))
      .then((res) => responseErrorCheck(res))
  }

  static async findProduct(findProductRequest: FindProductRequest, runId?: string) {
    return api.post<Product>(`${this.endpoint}/findProduct`, withRunId(findProductRequest, runId)).then((res) => res)
  }

  static async computeTags(
    operationId: string,
    identifiers: any[],
    workstationId: string,
    startEntityId?: string,
    startEntityCode?: string,
    operationAttributes?,
    itemAttributes?,
    runId?: string
  ) {
    const body: any = {
      operationId: operationId,
      identifiers: identifiers,
      workstationId: workstationId,
      operationAttributes: operationAttributes ?? {},
      itemAttributes: itemAttributes ?? {}
    }
    if (startEntityId) body.startEntityId = startEntityId
    if (startEntityCode) body.startCode = startEntityCode
    return api.post<any>(`${this.endpoint}/computeTags`, withRunId(body, runId)).then((res) => responseErrorCheck(res))
  }

  static async executeAction(
    operationId: string,
    action: string,
    vblParcelCode: string,
    wam: string,
    workstationId: string
  ) {
    const decodePayload = {
      operationId: operationId,
      actionCode: action,
      attributes: {
        vblParcelCode: vblParcelCode,
        wam: wam,
        workstationId: workstationId
      }
    }
    const res = await api.post(`${this.endpoint}/${operationId}/executeAction `, decodePayload)
    return responseErrorCheck(res)
  }

  static async findOrder(operationId: string, code: string, runId?: string) {
    const decodePayload = {
      operationId: operationId,
      code: code
    }
    return api
      .post<EncodingCounters>(`${this.endpoint}/findOrder`, withRunId(decodePayload, runId))
      .then((res) => responseErrorCheck(res))
  }

  static async tagPasswords(operationId: string, identifiers: TagPasswordIdentifiers[]) {
    const decodePayload = {
      operationId: operationId,
      identifiers
    }
    return api
      .post<TagPasswordResponse[]>(`${this.endpoint}/tagPasswords`, decodePayload)
      .then((res) => responseErrorCheck(res))
  }

  static async kill(operationId: string, tag?: TrmEncodingVerifyTag, runId?: string) {
    if (!tag) return false
    const decodePayload: any = {
      operationId: operationId,
      code: tag.code
    }
    if (tag.type === 'UHF' && tag.tid && tag.tid !== '') decodePayload.tid = tag.tid
    return api.post(`${this.endpoint}/kill`, withRunId(decodePayload, runId)).then((res) => responseErrorCheck(res))
  }

  static async replaceTag(
    operationId: string,
    oldTag: TrmEncodingVerifyReplaceTag,
    newTag: TrmEncodingVerifyReplaceTag,
    workstationId: string,
    runId?: string
  ) {
    const decodePayload: any = {
      operationId: operationId,
      oldTag,
      newTag,
      workstationId
    }
    return api
      .post(`${this.endpoint}/replaceTag`, withRunId(decodePayload, runId))
      .then((res) => responseErrorCheck(res))
  }

  static async userCounters(fromDate: string, toDate?: string, userId?: string) {
    const decodePayload: any = {
      fromDate: fromDate
    }
    if (toDate) decodePayload.toDate = toDate
    return api
      .get(`${this.encodingEndpoint}/progress/user${userId ? '/' + userId : ''}`, decodePayload)
      .then((res) => responseErrorCheck(res))
  }

  static async placeCounters(placeId: string, fromDate: string, toDate?: string) {
    if (!placeId) return []
    const decodePayload: any = {
      fromDate: fromDate
    }
    if (toDate) decodePayload.toDate = toDate
    return api
      .get(`${this.encodingEndpoint}/progress/place/${placeId}`, decodePayload)
      .then((res) => responseErrorCheck(res))
  }
}
